import { useCallback, useEffect, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  fetchFeaturedPosts,
  addFeaturedPost,
  removeFeaturedPost,
  reorderFeaturedPosts,
  setFeaturedImage,
} from '../api';

// Admin panel to curate "Featured on Instagram" posts.
//
// Instagram no longer lets us embed/scrape posts reliably from a third-party
// site, so we display the post's PHOTO (hosted in our own storage) as a tile
// that links to the real post. The admin pastes the post link and uploads its
// photo. A post with no photo won't appear on the site until one is added.

export default function AdminInstagram() {
  const { instance } = useMsal();
  const account = instance.getActiveAccount();
  const accountKey = account?.homeAccountId || account?.username || null;

  const [posts, setPosts] = useState([]);
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const fileInputs = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchFeaturedPosts();
      setPosts(res.posts || []);
    } catch {
      setErr('Could not load featured posts.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountKey]);

  useEffect(() => { load(); }, [load]);

  const onAdd = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setBusy(true); setErr(''); setMsg('');
    try {
      const r = await addFeaturedPost(instance, account, url.trim());
      if (r.ok) {
        setPosts(r.data.posts || []);
        setUrl('');
        setMsg('Post added — now add its photo below so it shows on the site.');
      } else {
        setErr(r.data?.message || 'Could not add that post.');
      }
    } catch {
      setErr('Could not add that post.');
    } finally {
      setBusy(false);
    }
  };

  const onPickImage = async (shortcode, file) => {
    if (!file) return;
    setBusy(true); setErr(''); setMsg('');
    try {
      const r = await setFeaturedImage(instance, account, shortcode, file);
      if (r.ok) { setPosts(r.data.posts || []); setMsg('Photo saved.'); }
      else setErr(r.data?.message || 'Could not save that photo.');
    } catch {
      setErr('Could not save that photo.');
    } finally {
      setBusy(false);
      if (fileInputs.current[shortcode]) fileInputs.current[shortcode].value = '';
    }
  };

  const onRemove = async (shortcode) => {
    setBusy(true); setErr(''); setMsg('');
    try {
      const r = await removeFeaturedPost(instance, account, shortcode);
      if (r.ok) { setPosts(r.data.posts || []); setMsg('Post removed.'); }
      else setErr(r.data?.message || 'Could not remove that post.');
    } catch {
      setErr('Could not remove that post.');
    } finally {
      setBusy(false);
    }
  };

  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= posts.length) return;
    const codes = posts.map((p) => p.shortcode);
    [codes[i], codes[j]] = [codes[j], codes[i]];
    setBusy(true); setErr(''); setMsg('');
    try {
      const r = await reorderFeaturedPosts(instance, account, codes);
      if (r.ok) setPosts(r.data.posts || []);
      else setErr(r.data?.message || 'Could not reorder.');
    } catch {
      setErr('Could not reorder.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card admin-instagram">
      <h2>Featured Instagram Posts</h2>
      <p className="subtle">
        Show favorite Instagram posts on the landing page. Paste the post link, then
        upload that post's photo — the site displays the photo as a tile that links to
        Instagram. (Instagram blocks live embeds from outside sites, so hosting the
        photo ourselves is what makes it reliable. Your account is untouched, so your
        Reels keep the full music library.)
      </p>

      <form className="ig-add-form" onSubmit={onAdd}>
        <input
          type="url"
          className="ig-url-input"
          placeholder="https://www.instagram.com/p/…"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          disabled={busy}
        />
        <button className="btn" type="submit" disabled={busy || !url.trim()}>
          {busy ? 'Working…' : 'Add post'}
        </button>
      </form>

      {msg && <p className="media-msg">{msg}</p>}
      {err && <p className="media-err">{err}</p>}
      {loading && <p className="subtle">Loading…</p>}
      {!loading && posts.length === 0 && <p className="subtle">No featured posts yet.</p>}

      <ol className="ig-admin-list">
        {posts.map((p, i) => (
          <li key={p.shortcode} className="ig-admin-row">
            <span className="ig-admin-num">{i + 1}</span>
            {p.imageUrl
              ? <img className="ig-admin-thumb" src={p.imageUrl} alt="" />
              : <span className="ig-admin-thumb ig-admin-thumb--empty" title="No photo yet">No photo</span>}
            <div className="ig-admin-main">
              <a className="ig-admin-url" href={p.url} target="_blank" rel="noopener noreferrer">
                {p.url}
              </a>
              {!p.imageUrl && (
                <span className="ig-admin-warn">⚠ Won't show on the site until you add a photo.</span>
              )}
              <label className="ig-admin-upload">
                {p.imageUrl ? 'Replace photo' : 'Add photo'}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/avif"
                  ref={(el) => { fileInputs.current[p.shortcode] = el; }}
                  onChange={(e) => onPickImage(p.shortcode, e.target.files?.[0])}
                  disabled={busy}
                  hidden
                />
              </label>
            </div>
            <div className="ig-admin-actions">
              <button className="btn-icon" onClick={() => move(i, -1)} disabled={busy || i === 0} aria-label="Move up">↑</button>
              <button className="btn-icon" onClick={() => move(i, 1)} disabled={busy || i === posts.length - 1} aria-label="Move down">↓</button>
              <button className="btn-link danger" onClick={() => onRemove(p.shortcode)} disabled={busy}>Remove</button>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}
